import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import TciRegistrySelect, { useTciRegistryOptions } from "components/fde/TciRegistrySelect";
import { useAuth } from "hooks/useAuth";
import { listAllClinicalDomains } from "services/fde/fdeRegistryService";
import {
  AUTONOMY_POSTURES,
  CMR_USAGE_MODES,
  FEEDBACK_SIGNAL_OPTIONS,
  REVIEW_CADENCES,
  getTciGovernanceProfile,
  saveTciGovernanceProfile,
} from "services/fde/tciGovernanceService";
import { userFacingLoadError } from "utils/firestoreErrors";

const STEPS = ["Clinical context", "Staff roles", "Clinical standard"];

const splitLines = (value) =>
  (value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const joinLines = (items) => (Array.isArray(items) ? items.join("\n") : "");

const TciInterrogatoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTciId = searchParams.get("tciId") || "";
  const { userData, organizationId, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;
  const canManage =
    Boolean(isGlobalAdmin) ||
    (userData?.role === "admin" && Boolean(institutionId));

  const [activeStep, setActiveStep] = useState(0);
  const { tcis, reload: reloadTcis } = useTciRegistryOptions({ institutionId });
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profileStatus, setProfileStatus] = useState("configuring");

  const { control, register, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      tciId: initialTciId,
      domainId: "",
      annualVolume: "",
      conductingClinicians: "",
      primaryGoal: "",
      failureDefinition: "",
      currentAiUse: "",
      fdeRoleDiscoveryNotes: "",
      mustDoText: "",
      mustNotDoText: "",
      feedbackSignals: [],
      populationNotes: "",
      constraintNotes: "",
      autonomyPosture: "balanced",
      reviewCadence: "monthly",
      candidateNudgeThreshold: 2,
      cmrUsageMode: "",
    },
  });

  const selectedTciId = watch("tciId");
  const selectedDomainId = watch("domainId");

  const domainOptions = useMemo(() => {
    if (!selectedTciId) return domains;
    return domains.filter((domain) =>
      (domain.tciIds || []).includes(selectedTciId),
    );
  }, [domains, selectedTciId]);

  const loadData = useCallback(async () => {
    if (!institutionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const domainRows = await listAllClinicalDomains({ institutionId });
      setDomains(domainRows);
      const tciRows = await reloadTcis();

      const tciId = initialTciId || tciRows[0]?.id || "";
      if (tciId) {
        const profile = await getTciGovernanceProfile({ institutionId, tciId });
        if (profile) {
          reset({
            tciId: profile.tciId,
            domainId: profile.domainId || "",
            annualVolume: profile.annualVolume ?? "",
            conductingClinicians: profile.conductingClinicians || "",
            primaryGoal: profile.primaryGoal || profile.patientExperienceGoal || "",
            failureDefinition: profile.failureDefinition || "",
            currentAiUse: profile.currentAiUse || "",
            fdeRoleDiscoveryNotes: profile.fdeRoleDiscoveryNotes || "",
            mustDoText: joinLines(profile.mustDo),
            mustNotDoText: joinLines(profile.mustNotDo),
            feedbackSignals: profile.feedbackSignals || [],
            populationNotes: profile.populationNotes || "",
            constraintNotes: profile.constraintNotes || "",
            autonomyPosture: profile.autonomyPosture || "balanced",
            reviewCadence: profile.reviewCadence || "monthly",
            candidateNudgeThreshold: profile.candidateNudgeThreshold ?? 2,
            cmrUsageMode: profile.cmrUsageMode || "",
          });
          setProfileStatus(profile.status || "configuring");
          const completed = profile.passesCompleted || {};
          if (!completed.pass1) setActiveStep(0);
          else if (!completed.pass2) setActiveStep(1);
          else if (!completed.pass3) setActiveStep(2);
        } else if (tciId) {
          setValue("tciId", tciId);
        }
      }
    } catch (err) {
      const msg = userFacingLoadError(err, "Failed to load interrogatory data.");
      if (msg) setError(msg);
    } finally {
      setLoading(false);
    }
  }, [institutionId, initialTciId, reset, setValue, reloadTcis]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedDomainId && domainOptions.length === 1) {
      setValue("domainId", domainOptions[0].domainId || domainOptions[0].id);
    }
  }, [domainOptions, selectedDomainId, setValue]);

  const onSavePass = async (values, pass) => {
    if (!institutionId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await saveTciGovernanceProfile({
        institutionId,
        tciId: values.tciId,
        domainId: values.domainId,
        pass,
        annualVolume: values.annualVolume,
        conductingClinicians: values.conductingClinicians,
        primaryGoal: values.primaryGoal,
        patientExperienceGoal: values.primaryGoal,
        failureDefinition: values.failureDefinition,
        currentAiUse: values.currentAiUse,
        fdeRoleDiscoveryNotes: values.fdeRoleDiscoveryNotes,
        mustDo: splitLines(values.mustDoText),
        mustNotDo: splitLines(values.mustNotDoText),
        feedbackSignals: values.feedbackSignals,
        populationNotes: values.populationNotes,
        constraintNotes: values.constraintNotes,
        autonomyPosture: values.autonomyPosture,
        reviewCadence: values.reviewCadence,
        candidateNudgeThreshold: Number(values.candidateNudgeThreshold) || 2,
        ...(values.cmrUsageMode ? { cmrUsageMode: values.cmrUsageMode } : {}),
      });
      setProfileStatus(result.profileStatus || "configuring");
      setSuccess("Progress saved.");
      if (pass < 3) {
        setActiveStep(pass);
      } else {
        navigate(`/dashboard/tci-governance/${values.tciId}`);
      }
    } catch (err) {
      setError(err?.message || "Failed to save governance profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading page />;
  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Institution admin access is required to run the TCI governance interrogatory.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: "auto" }}>
      <DashboardPageHeader
        title="TCI governance interrogatory"
        subtitle="Configure the clinical standard and feedback loop for a TCI at your institution."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Profile status:
        </Typography>
        <Chip size="small" label={profileStatus} color={profileStatus === "active" ? "success" : "default"} />
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box component="form" noValidate>
        {activeStep === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Controller
              name="tciId"
              control={control}
              rules={{ required: "Select a TCI" }}
              render={({ field, fieldState }) => (
                <TciRegistrySelect
                  label="TCI"
                  institutionId={institutionId}
                  value={field.value || ""}
                  onChange={field.onChange}
                  error={Boolean(fieldState.error)}
                  required
                />
              )}
            />
            <Controller
              name="domainId"
              control={control}
              rules={{ required: "Select a clinical domain" }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={Boolean(fieldState.error)}>
                  <InputLabel id="domain-select-label">Clinical domain</InputLabel>
                  <Select {...field} labelId="domain-select-label" label="Clinical domain">
                    {domainOptions.map((domain) => (
                      <MenuItem key={domain.domainId || domain.id} value={domain.domainId || domain.id}>
                        {domain.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <TextField
              label="Annual volume (approximate encounters)"
              type="number"
              {...register("annualVolume")}
            />
            <TextField
              label="Conducting clinicians"
              multiline
              minRows={2}
              {...register("conductingClinicians")}
            />
            <TextField
              label="Primary goal for AI in this TCI"
              multiline
              minRows={2}
              {...register("primaryGoal", { required: true })}
            />
            <TextField
              label="What would failure look like?"
              multiline
              minRows={2}
              {...register("failureDefinition", { required: true })}
            />
            <TextField
              label="Current AI use (if any)"
              multiline
              minRows={2}
              {...register("currentAiUse")}
            />
            <Controller
              name="cmrUsageMode"
              control={control}
              rules={{ required: "Select how this TCI uses CMR" }}
              render={({ field, fieldState }) => (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    CMR (MasterChart) usage
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Does this TCI read or write the patient&apos;s portable medical record? If so,
                    choose how CMR data elements (medications, allergies, conditions, or other)
                    enter the AI session that annotators and the TCI Manager review. This is a
                    governance record only — it does not change access rules yet.
                  </Typography>
                  {fieldState.error ? (
                    <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
                      {fieldState.error.message}
                    </Typography>
                  ) : null}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {CMR_USAGE_MODES.map((mode) => (
                      <SelectableChoiceCard
                        key={mode.value}
                        selected={field.value === mode.value}
                        onClick={() => field.onChange(mode.value)}
                        title={mode.label}
                        description={mode.description}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            />
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Document which staffing roles exist today, who fills them, and any gaps before the loop can
              operate.
            </Typography>
            <TextField
              label="Role discovery"
              multiline
              minRows={8}
              placeholder="Example: Clinical Annotator assigned for triage in ED; TCI Manager is pharmacy director; no Technical Readiness Lead yet."
              {...register("fdeRoleDiscoveryNotes", { required: true })}
            />
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Must do (one per line)"
              multiline
              minRows={4}
              {...register("mustDoText", { required: true })}
            />
            <TextField
              label="Must not do (one per line)"
              multiline
              minRows={4}
              {...register("mustNotDoText", { required: true })}
            />
            <Controller
              name="feedbackSignals"
              control={control}
              render={({ field }) => (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Feedback signals
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {FEEDBACK_SIGNAL_OPTIONS.map((signal) => {
                      const selected = field.value.includes(signal);
                      return (
                        <SelectableChoiceCard
                          key={signal}
                          selected={selected}
                          onClick={() => {
                            const next = selected
                              ? field.value.filter((item) => item !== signal)
                              : [...field.value, signal];
                            field.onChange(next);
                          }}
                          title={signal}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            />
            <Controller
              name="autonomyPosture"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="posture-label">Autonomy posture</InputLabel>
                  <Select {...field} labelId="posture-label" label="Autonomy posture">
                    {AUTONOMY_POSTURES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="reviewCadence"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="cadence-label">Review cadence</InputLabel>
                  <Select {...field} labelId="cadence-label" label="Review cadence">
                    {REVIEW_CADENCES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <TextField
              label="Candidate nudge threshold"
              type="number"
              inputProps={{ min: 1, max: 50 }}
              {...register("candidateNudgeThreshold")}
            />
            <TextField
              label="Population notes"
              multiline
              minRows={2}
              {...register("populationNotes")}
            />
            <TextField
              label="Constraint notes"
              multiline
              minRows={2}
              {...register("constraintNotes")}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <LoadingButton
            disabled={activeStep === 0 || saving}
            onClick={() => setActiveStep((step) => Math.max(0, step - 1))}
          >
            Back
          </LoadingButton>
          <LoadingButton
            variant="contained"
            loading={saving}
            onClick={handleSubmit((values) => onSavePass(values, activeStep + 1))}
          >
            {activeStep === STEPS.length - 1 ? "Save & finish" : "Save & continue"}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export default TciInterrogatoryPage;
